import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Summary from '../../components/summary/summary';
import MobileLayout from '../../components/layout/MobileLayout';
import { Box } from '@mui/material';
import { fetchConversationDetails } from '../../utils/axiosApiRequest';
import { useTranslation } from 'react-i18next';

const MobileSummaryPage = ({
  summary,
  setSummary,
  summaryId,
  setSummaryId,
  setGeneratingSummary,
  audioFilename,
  setAudioFilename,
  conversation,
  promptTemplates,
  selectedPrompt,
  setSelectedPrompt,
  startRecording,
  stopRecording,
  recording,
  audioReady,
  recordingDuration,
  wordCount,
  messages,
  generatingSummary,
  setShowControls,
  setSelectedConversation
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  // Fetch conversation data if not available after page refresh
  useEffect(() => {
    let isMounted = true;
    
    const fetchConversation = async () => {
      if (!conversationId) {
        console.log('No conversation ID in URL');
        navigate('/', { replace: true });
        return;
      }
      
      // Skip fetching if correct conversation data is already available
      if (conversation && conversation.id === conversationId) {
        setIsLoading(false);
        return;
      }
      
      try {
        const details = await fetchConversationDetails(conversationId);
        if (!isMounted) return;
        
        if (details) {
          setSelectedConversation({
            id: conversationId,
            ...details
          });
          
          if (details.audio_filename) {
            setAudioFilename(details.audio_filename);
          }
          
          if (details.summary) {
            setSummary(details.summary);
          }
          
          setIsLoading(false);
        } else {
          console.log('No conversation data found');
          navigate('/', { replace: true });
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching conversation:', error);
          navigate('/', { replace: true });
        }
      }
    };
    
    fetchConversation();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [conversationId, navigate, setSelectedConversation]);

  // Reset previous audio elements
  useEffect(() => {
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load();
    });
    
    // Return cleanup function
    return () => {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    };
  }, [audioFilename]);

  const handleStartRecording = () => {
    setShowControls(true);
    startRecording();
  };

  if (isLoading) {
    return (
      <MobileLayout title={t('mobileTitle.summary')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          {t('common.loadingConversation')}
        </Box>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title={t('mobileTitle.summary')}>
      <Box sx={{ height: '100%', overflow: 'hidden' }}>
        <Summary
          summary={summary || ''}
          setSummary={setSummary}
          summaryId={summaryId}
          setSummaryId={setSummaryId}
          setGeneratingSummary={setGeneratingSummary}
          conversation={conversation}
          audioFilename={audioFilename}
          selectedPrompt={selectedPrompt}
          setSelectedPrompt={setSelectedPrompt}
          promptTemplates={promptTemplates}
          startRecording={handleStartRecording}
          stopRecording={stopRecording}
          recording={recording}
          audioReady={audioReady}
          recordingDuration={recordingDuration}
          wordCount={wordCount}
          messages={messages}
          generatingSummary={generatingSummary}
          isMobile={true}
        />
      </Box>
    </MobileLayout>
  );
};

export default MobileSummaryPage; 